import { ErrorHandler, Injectable } from '@angular/core';

/**
 * A deploy replaces the app's hashed chunk files. A browser still holding the previous
 * index.html then asks for a chunk that no longer exists. It does NOT get a 404: server.js
 * serves index.html for any unmatched path, so the request returns 200 with HTML, and webpack
 * — expecting JavaScript — raises ChunkLoadError. That is why detection keys off the error
 * name and not just the message. Reloading is safe: index.html is served no-store, so the
 * browser picks up the new build.
 *
 * The sessionStorage guard exists because a chunk that stays unresolvable would otherwise
 * reload the page in an infinite loop.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private static readonly RELOAD_GUARD_KEY = 'chunk-reload-attempt';
  private static readonly RELOAD_GUARD_WINDOW_MS = 10_000;

  handleError(error: unknown): void {
    if (this.isChunkLoadError(error) && this.shouldReload()) {
      sessionStorage.setItem(GlobalErrorHandler.RELOAD_GUARD_KEY, String(Date.now()));
      this.reload();
      return;
    }

    console.error(error);
  }

  protected reload(): void {
    window.location.reload();
  }

  private isChunkLoadError(error: unknown): boolean {
    const name = (error as { name?: unknown })?.name;
    if (name === 'ChunkLoadError') {
      return true;
    }

    const rawMessage = (error as { message?: unknown })?.message;
    const message = typeof rawMessage === 'string' ? rawMessage : String(error ?? '');

    return /Loading (CSS )?chunk .+ failed/i.test(message)
      || /Failed to fetch dynamically imported module/i.test(message)
      || /error loading dynamically imported module/i.test(message);
  }

  private shouldReload(): boolean {
    const previous = Number(sessionStorage.getItem(GlobalErrorHandler.RELOAD_GUARD_KEY));

    if (!Number.isFinite(previous) || previous <= 0) {
      return true;
    }

    return Date.now() - previous > GlobalErrorHandler.RELOAD_GUARD_WINDOW_MS;
  }
}
